/**
 * Improvements List Component
 *
 * Shows prioritized improvement recommendations with severity indicators.
 *
 * Layout:
 *   [HIGH]   Add quantified achievements to bullet points
 *            Why: ATS systems rank resumes higher with measurable results
 *   [MEDIUM] Use stronger action verbs
 *            Why: Active voice demonstrates leadership
 */

import React from 'react';
import { TrendingUp } from 'lucide-react';
import { cx } from '../utils/score-helpers';
import type { ImprovementsListProps } from '../types';
import type { ImprovementPriority } from '../../../types/ats-report';

const priorityLabels: Record<ImprovementPriority, string> = {
  high: 'HIGH',
  medium: 'MEDIUM',
  low: 'LOW',
};

export const ImprovementsList: React.FC<ImprovementsListProps> = ({ improvements }) => {
  if (improvements.length === 0) return null;

  // Sort: high first, then medium, then low
  const sorted = [...improvements].sort((a, b) => {
    const order: Record<ImprovementPriority, number> = { high: 0, medium: 1, low: 2 };
    return order[a.priority] - order[b.priority];
  });

  return (
    <div className="ats-report-improvements">
      <div className="ats-report-improvements__header">
        <TrendingUp className="ats-report-improvements__header-icon" />
        <h4 className="ats-report-improvements__title">Improvements</h4>
        <span className="ats-report-improvements__count">{improvements.length}</span>
      </div>

      <ul className="ats-report-improvements__list">
        {sorted.map((item, i) => (
          <li
            key={i}
            className={cx(
              'ats-report-improvement',
              `ats-report-improvement--${item.priority}`,
            )}
          >
            <span className={cx(
              'ats-report-improvement__badge',
              `ats-report-improvement__badge--${item.priority}`,
            )}>
              {priorityLabels[item.priority]}
            </span>
            <div className="ats-report-improvement__content">
              <span className="ats-report-improvement__action">{item.action}</span>
              {item.reason && (
                <span className="ats-report-improvement__reason">
                  Why: {item.reason}
                </span>
              )}
              {item.category && (
                <span className="ats-report-improvement__category">{item.category}</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

ImprovementsList.displayName = 'ImprovementsList';

export default ImprovementsList;
